import React, { useState, useEffect, useRef } from 'react';
import { FileText, Mic, MicOff, Loader2, CheckCircle, Edit3, Save, X, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import './ConsultationNotes.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ConsultationNotes Component
 * Handles audio recording, transcription, and SOAP note generation
 */

// Audio Recorder Hook
export function useAudioRecorder() {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const startRecording = async () => {
    try {
      setError(null);
      setAudioBlob(null);
      chunksRef.current = [];
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      
      streamRef.current = stream;
      
      // Use webm for better compatibility
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus' 
        : 'audio/webm';
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setAudioBlob(blob);
        
        // Stop all tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }
      };
      
      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(t => t + 1);
      }, 1000);
      
    } catch (err) {
      console.error('Recording error:', err);
      setError('Impossible d\'accéder au microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const resetRecording = () => {
    setAudioBlob(null);
    setRecordingTime(0);
    setError(null);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  return {
    isRecording,
    audioBlob,
    recordingTime,
    error,
    startRecording,
    stopRecording,
    resetRecording
  };
}

// Format seconds to MM:SS
function formatTime(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

// SOAP Section Editor
function SOAPSection({ title, content, onEdit, isEditing, editValue, onEditChange, onSave, onCancel }) {
  const [isExpanded, setIsExpanded] = useState(true);
  
  return (
    <div className="soap-section">
      <div className="soap-section-header" onClick={() => setIsExpanded(!isExpanded)}>
        <h4>{title}</h4>
        <div className="soap-section-actions">
          {!isEditing && (
            <button 
              onClick={(e) => { e.stopPropagation(); onEdit(); }} 
              className="btn-icon"
              title="Modifier"
            >
              <Edit3 size={16} />
            </button>
          )}
          {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </div>
      
      {isExpanded && (
        <div className="soap-section-content">
          {isEditing ? (
            <div className="soap-edit-mode">
              <textarea
                value={editValue}
                onChange={(e) => onEditChange(e.target.value)}
                rows={6}
                autoFocus
              />
              <div className="soap-edit-actions">
                <button onClick={onSave} className="btn-save">
                  <Save size={14} /> Enregistrer
                </button>
                <button onClick={onCancel} className="btn-cancel">
                  <X size={14} /> Annuler
                </button>
              </div>
            </div>
          ) : (
            <div className="soap-text">
              {content || <span className="text-muted">Non renseigné</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Main Consultation Notes Component
export default function ConsultationNotes({ 
  appointmentId, 
  doctorToken, 
  patientInfo,
  prefilledData, // New: data from automatic transcription/generation
  onNoteSaved,
  onClose 
}) {
  const [step, setStep] = useState(prefilledData ? 'editing' : 'record'); // record, transcribing, editing, saving
  const [transcription, setTranscription] = useState(prefilledData?.transcription || '');
  const [soapNote, setSoapNote] = useState(prefilledData?.soapNote || {
    subjective: '',
    objective: '',
    assessment: '',
    plan: ''
  });
  const [rawNote, setRawNote] = useState(prefilledData?.rawNote || '');
  const [editingSection, setEditingSection] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showTranscription, setShowTranscription] = useState(false);
  const [autoGenerated, setAutoGenerated] = useState(!!prefilledData); // Track if note was auto-generated
  
  const {
    isRecording,
    audioBlob,
    recordingTime,
    error: recordingError,
    startRecording,
    stopRecording,
    resetRecording
  } = useAudioRecorder();

  // Load existing note if available (only on mount, no prefilled data)
  // NOTE: intentionally only depends on []: re-running this effect after a
  // successful save (when the parent clears prefilledData) would refetch the
  // just-saved note and race with setStep('saved'), leaving the user back on
  // the editing screen — making the "Valider" button look broken.
  useEffect(() => {
    if (!prefilledData) {
      loadExistingNote();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadExistingNote = async () => {
    try {
      const response = await fetch(`${API_URL}/api/consultation/note/${appointmentId}`, {
        headers: {
          'Authorization': `Bearer ${doctorToken}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.found && data.note) {
          setTranscription(data.note.transcription || '');
          setSoapNote(data.note.soap_note || {
            subjective: '',
            objective: '',
            assessment: '',
            plan: ''
          });
          setStep('editing');
        }
      }
    } catch (err) {
      console.error('Error loading note:', err);
    }
  };

  // Transcribe audio
  const handleTranscribe = async () => {
    if (!audioBlob) return;
    
    setStep('transcribing');
    setError(null);
    setIsLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('appointment_id', appointmentId);
      
      const response = await fetch(`${API_URL}/api/consultation/transcribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${doctorToken}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Erreur de transcription');
      }
      
      const data = await response.json();
      setTranscription(data.transcription);
      
      // Now generate SOAP note
      await generateSOAPNote(data.transcription);
      
    } catch (err) {
      console.error('Transcription error:', err);
      setError(err.message);
      setStep('record');
    } finally {
      setIsLoading(false);
    }
  };

  // Generate SOAP note from transcription
  const generateSOAPNote = async (text) => {
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/consultation/generate-note`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${doctorToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          transcription: text,
          appointment_id: appointmentId,
          patient_info: patientInfo
        })
      });
      
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Erreur de génération');
      }
      
      const data = await response.json();
      setSoapNote(data.soap_note);
      setRawNote(data.raw_note);
      setStep('editing');
      
    } catch (err) {
      console.error('SOAP generation error:', err);
      setError(err.message);
      // Still go to editing with empty note
      setStep('editing');
    } finally {
      setIsLoading(false);
    }
  };

  // Save note
  const saveNote = async (validated = false) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/consultation/save-note`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${doctorToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          appointment_id: appointmentId,
          transcription: transcription,
          soap_note: soapNote,
          validated: validated
        })
      });
      
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Erreur d\'enregistrement');
      }
      
      const data = await response.json();
      
      if (onNoteSaved) {
        onNoteSaved(data);
      }
      
      if (validated) {
        setStep('saved');
      }
      
    } catch (err) {
      console.error('Save error:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Edit section handlers
  const startEditSection = (section) => {
    setEditingSection(section);
    setEditValue(soapNote[section] || '');
  };

  const saveEditSection = () => {
    setSoapNote(prev => ({
      ...prev,
      [editingSection]: editValue
    }));
    setEditingSection(null);
    setEditValue('');
  };

  const cancelEditSection = () => {
    setEditingSection(null);
    setEditValue('');
  };

  // Render based on step
  return (
    <div className="consultation-notes-container" data-testid="consultation-notes">
      <div className="consultation-notes-header">
        <div className="header-title">
          <FileText size={24} />
          <h3>Note de Consultation</h3>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="btn-close"
            title="Fermer"
          >
            <X size={20} />
          </button>
        )}
      </div>

      {error && (
        <div className="notes-error">
          <AlertTriangle size={18} />
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Step 1: Recording */}
      {step === 'record' && (
        <div className="notes-step record-step">
          <div className="step-intro">
            <p>Enregistrez la consultation pour générer automatiquement une note SOAP.</p>
          </div>
          
          <div className="recorder-container">
            {!audioBlob ? (
              <>
                <div className={`recorder-visual ${isRecording ? 'recording' : ''}`}>
                  {isRecording ? (
                    <>
                      <div className="recording-pulse"></div>
                      <Mic size={48} className="mic-icon active" />
                    </>
                  ) : (
                    <MicOff size={48} className="mic-icon" />
                  )}
                </div>
                
                {isRecording && (
                  <div className="recording-time">
                    <span className="time-display">{formatTime(recordingTime)}</span>
                    <span className="recording-label">Enregistrement en cours...</span>
                  </div>
                )}
                
                <div className="recorder-actions">
                  {!isRecording ? (
                    <button 
                      onClick={startRecording} 
                      className="btn-record"
                      data-testid="start-recording-btn"
                    >
                      <Mic size={20} />
                      Démarrer l'enregistrement
                    </button>
                  ) : (
                    <button 
                      onClick={stopRecording} 
                      className="btn-stop"
                      data-testid="stop-recording-btn"
                    >
                      <MicOff size={20} />
                      Arrêter ({formatTime(recordingTime)})
                    </button>
                  )}
                </div>
              </>
            ) : (
              <div className="recording-ready">
                <CheckCircle size={48} className="success-icon" />
                <p>Enregistrement terminé ({formatTime(recordingTime)})</p>
                
                <div className="recording-actions">
                  <button 
                    onClick={handleTranscribe} 
                    className="btn-primary"
                    data-testid="transcribe-btn"
                  >
                    <FileText size={18} />
                    Transcrire et générer la note
                  </button>
                  <button 
                    onClick={resetRecording} 
                    className="btn-secondary"
                  >
                    Recommencer
                  </button>
                </div>
              </div>
            )}
            
            {recordingError && (
              <p className="recording-error">{recordingError}</p>
            )}
          </div>

          {/* Manual entry option */}
          <div className="manual-entry-option">
            <p>— OU —</p>
            <button
              onClick={() => setStep('editing')}
              className="btn-link"
              data-testid="manual-note-btn"
            >
              <Edit3 size={16} /> Saisir manuellement la note
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Transcribing */}
      {step === 'transcribing' && (
        <div className="notes-step transcribing-step">
          <div className="loading-container">
            <Loader2 size={48} className="animate-spin" />
            <h4>Traitement en cours...</h4>
            <p>Transcription de l'audio et génération de la note SOAP</p>
            <p className="loading-detail">Cela peut prendre quelques instants...</p>
          </div>
        </div>
      )}

      {/* Step 3: Editing SOAP Note */}
      {step === 'editing' && (
        <div className="notes-step editing-step">
          {/* Auto-generated banner */}
          {autoGenerated && (
            <div className="auto-generated-banner">
              <CheckCircle size={18} />
              <span>Note générée automatiquement à partir de l'enregistrement de la consultation. Veuillez vérifier et valider.</span>
            </div>
          )}
          
          {/* Transcription toggle */}
          {transcription && (
            <div className="transcription-section">
              <button 
                onClick={() => setShowTranscription(!showTranscription)}
                className="btn-toggle-transcription"
              >
                {showTranscription ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                {showTranscription ? 'Masquer' : 'Afficher'} la transcription
              </button>
              
              {showTranscription && (
                <div className="transcription-content">
                  <p>{transcription}</p>
                </div>
              )}
            </div>
          )}

          {/* SOAP Sections */}
          <div className="soap-sections">
            <SOAPSection
              title="S - Subjectif (Anamnèse)"
              content={soapNote.subjective}
              isEditing={editingSection === 'subjective'}
              editValue={editValue}
              onEdit={() => startEditSection('subjective')}
              onEditChange={setEditValue}
              onSave={saveEditSection}
              onCancel={cancelEditSection}
            />
            
            <SOAPSection
              title="O - Objectif (Examen)"
              content={soapNote.objective}
              isEditing={editingSection === 'objective'}
              editValue={editValue}
              onEdit={() => startEditSection('objective')}
              onEditChange={setEditValue}
              onSave={saveEditSection}
              onCancel={cancelEditSection}
            />
            
            <SOAPSection
              title="A - Assessment (Diagnostic)"
              content={soapNote.assessment}
              isEditing={editingSection === 'assessment'}
              editValue={editValue}
              onEdit={() => startEditSection('assessment')}
              onEditChange={setEditValue}
              onSave={saveEditSection}
              onCancel={cancelEditSection}
            />
            
            <SOAPSection
              title="P - Plan (Traitement)"
              content={soapNote.plan}
              isEditing={editingSection === 'plan'}
              editValue={editValue}
              onEdit={() => startEditSection('plan')}
              onEditChange={setEditValue}
              onSave={saveEditSection}
              onCancel={cancelEditSection}
            />
          </div>

          {/* Actions */}
          <div className="notes-actions">
            <button 
              onClick={() => saveNote(false)} 
              className="btn-secondary"
              disabled={isLoading}
            >
              <Save size={18} />
              Enregistrer (brouillon)
            </button>
            <button 
              onClick={() => saveNote(true)} 
              className="btn-primary"
              disabled={isLoading}
              data-testid="validate-note-btn"
            >
              {isLoading ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle size={18} />}
              Valider et ajouter au dossier
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Saved confirmation */}
      {step === 'saved' && (
        <div className="notes-step saved-step">
          <div className="success-container">
            <CheckCircle size={64} className="success-icon" />
            <h4>Note enregistrée avec succès</h4>
            <p>La note de consultation a été ajoutée au dossier du patient.</p>
            
            <div className="saved-actions">
              <button onClick={onClose} className="btn-primary">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

